import { ClockIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import { de } from "date-fns/locale";

import { useScrollFade } from "~/hooks/useScrollFade.ts";
import type { CalendarActions, CalendarDay } from "~/modules/dashboard/components/TimeTrackingCalendar/calendarDays.ts";
import { HolidayAndAbsenceBars } from "~/modules/dashboard/components/TimeTrackingCalendar/HolidayAndAbsenceBars.tsx";
import { TrackingCard } from "~/modules/dashboard/components/TimeTrackingCalendar/TrackingCard.tsx";
import { TrackingEntryRow } from "~/modules/dashboard/components/TimeTrackingCalendar/TrackingEntryRow.tsx";
import { AddTimeTrackingButton } from "~/modules/timeTracking/components/components/AddTimeTrackingButton.tsx";
import HourCount from "~/modules/timeTracking/components/components/Day/components/HourCount/HourCount.tsx";
import { getDayTotalHours } from "~/modules/timeTracking/utils/timeTrackingUtils.ts";
import { formatNumberWithComma } from "~/utils/numberUtils.ts";

type Props = CalendarActions & {
	day: CalendarDay;
	// The phone body shows the "+" as a primary button.
	primaryAdd?: boolean;
	// The style of "Keine Einträge" on a day without entries: plain text on the phone, a row in the day view. None while the data loads.
	emptyHint?: "text" | "block";
	// The day view shows the day as a card: a date band, the entries, and a footer with the "+".
	card?: boolean;
	// The backend marks the day as `booked`. The day total is green then, else gray.
	isBooked?: boolean;
};

/** The selected day below the compact grid: its holiday, its absences, its trackings and its "+". */
export function DayList(props: Props) {
	const {
		absenceActions,
		day,
		primaryAdd,
		emptyHint,
		card,
		isBooked,
		canAddTrackingOn,
		canAddAbsence,
		showDisabledAddTracking,
		openCreate,
		openUpdate,
		openDelete,
		selectedTimeTrackingId,
		setSelectedTimeTrackingId,
	} = props;
	const canAddEntry = canAddAbsence || canAddTrackingOn(day.date);
	const close = () => setSelectedTimeTrackingId(null);
	const { ref: scrollRef, style: scrollStyle } = useScrollFade<HTMLDivElement>();
	const isEmpty = day.timeTrackings.length === 0 && day.absences.length === 0;

	const date = (
		// The header of the calendar shows only the month in each view, so the day list holds the date.
		<div className="flex items-center justify-between gap-2">
			<h4 className={card ? "min-w-0 text-sm font-semibold text-gray-900" : "min-w-0 text-sm font-semibold text-gray-700"}>{format(day.date, "EEEE, d. MMMM", { locale: de })}</h4>
			{day.timeTrackings.length > 0 && (
				<div className="shrink-0">
					<HourCount theme={isBooked ? "completed" : undefined}
							   size="sm"
							   shape="pill">
						{formatNumberWithComma(getDayTotalHours({ timeTrackings: day.timeTrackings, absences: day.absences }), 1, false)} h
					</HourCount>
				</div>
			)}
		</div>
	);
	const entries = (
		<>
			<HolidayAndAbsenceBars day={day}
							   absenceActions={absenceActions}
							   inPlace />
			{isEmpty && emptyHint === "text" && <p className="text-sm text-gray-500">Keine Einträge</p>}
			{isEmpty && emptyHint === "block" && (
				<div className="flex items-center gap-2 rounded-lg bg-gray-50 p-3 text-sm text-gray-500">
					<ClockIcon className="size-5 text-gray-400"
							   aria-hidden="true" />
					<p>Keine Einträge an diesem Tag</p>
				</div>
			)}
			{day.timeTrackings.map((timeTracking) => timeTracking.id === selectedTimeTrackingId ? (
				<TrackingCard key={timeTracking.id}
							  timeTracking={timeTracking}
							  onClickEdit={() => {
								  close();
								  openUpdate(timeTracking.id);
							  }}
							  onClickDelete={() => {
								  close();
								  openDelete(timeTracking.id);
							  }}
							  onClickClose={close} />
			) : (
				<TrackingEntryRow key={timeTracking.id}
								  timeTracking={timeTracking}
								  onClick={() => setSelectedTimeTrackingId(timeTracking.id)} />
			))}
		</>
	);
	const showAdd = canAddEntry || showDisabledAddTracking;
	const addButton = showAdd && (
		<AddTimeTrackingButton date={day.date}
							   canAddTracking={canAddEntry}
							   openCreate={openCreate}
							   className={primaryAdd ? "mt-3" : undefined}
							   primary={primaryAdd}
							   label={primaryAdd ? undefined : "Neuer Eintrag"} />
	);

	if (card) {
		return (
			<div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-gray-200 bg-white">
				<div className="shrink-0 border-b border-gray-200 bg-gray-50 px-4 py-3">{date}</div>
				{/* The card keeps the height that the parent gives it, so a long day scrolls inside the card. */}
				<div ref={scrollRef}
					 style={scrollStyle}
					 className="min-h-0 flex-1 scroll-py-8 overflow-y-auto">
					<div className="flex flex-col gap-3 p-4">{entries}</div>
				</div>
				{showAdd && <div className="shrink-0 border-t border-gray-200 p-4">{addButton}</div>}
			</div>
		);
	}

	return (
		<div className="flex min-w-0 flex-col gap-3">
			{date}
			{entries}
			{addButton}
		</div>
	);
}
